import { Col, Popover, Row, Tooltip, Typography } from 'antd';

import {
  CheckOutlined,
  CloseOutlined,
  QuestionCircleTwoTone,
  QuestionCircleOutlined,
} from '@ant-design/icons';

import { useRequiredFieldsValidation } from './hooks';

import { FullWidthSpace } from 'common/components';

import { useGetQaSpecDataHeader } from 'pages/qa-spec/query';
import { useGetQaSpecRequiredFieldsInQaSpecFullView } from 'pages/qa-spec/hooks/useGetQaSpecRequiredFields';

import {
  QAS_REQUIRED_FIELD_ADDITIONAL_GROUP_MAPS,
  QAS_REQUIRED_FIELD_HIDDEN_GROUP_MAPS,
} from 'pages/qas-require-field/constants';

export const RequiredFieldsError = ({ productId, children }) => {
  const { data: requiredFields } = useGetQaSpecRequiredFieldsInQaSpecFullView({
    productId,
  });

  const CONTACT_SECTION_FIELDS = [
    'Primary QA Contact',
    '24 Hour Contact',
    'Sales Contact',
    'Production Facility',
  ];

  const requiredContactFields = requiredFields?.requiredContactFields ?? [];

  const areContactsValid = CONTACT_SECTION_FIELDS.every(
    (field) => !requiredContactFields.includes(field)
  );

  const { isAllFieldsValid } = useRequiredFieldsValidation(productId);

  // Overall validity includes both
  const isOverallValid = isAllFieldsValid && areContactsValid;
  const shouldShowValidateIcon = requiredFields?.length > 0;

  return shouldShowValidateIcon ? (
    <Popover
      placement='left'
      title={null}
      content={<RequiredFieldsContent productId={productId} />}
      trigger='hover'
    >
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <QuestionCircleTwoTone
          twoToneColor={isOverallValid ? '#52c41a' : '#faad14'}
          style={{ fontSize: 18, paddingRight: 6 }}
        />
        {children}
      </div>
    </Popover>
  ) : null;
};

const RequiredFieldsContent = ({ productId }) => {
  const { checkValidRequiredField, groupRequiredFields } =
    useRequiredFieldsValidation(productId);

  const CONTACT_SECTION_FIELDS = [
    'Primary QA Contact',
    '24 Hour Contact',
    'Sales Contact',
    'Production Facility',
  ];

  const { qaSpecDataHeader } = useGetQaSpecDataHeader({
    id: productId,
    isEnabled: false,
  });

  //  get requiredContactFields from the hook
  const { data: requiredFields } = useGetQaSpecRequiredFieldsInQaSpecFullView({
    productId,
  });
  const requiredContactFields = requiredFields?.requiredContactFields ?? [];

  const additionalGroupList = Object.values(
    QAS_REQUIRED_FIELD_ADDITIONAL_GROUP_MAPS
  ).flat();
  const productType = qaSpecDataHeader?.type;
  const additionalGroup = QAS_REQUIRED_FIELD_ADDITIONAL_GROUP_MAPS[productType];
  const hiddenGroups = QAS_REQUIRED_FIELD_HIDDEN_GROUP_MAPS[productType];

  return (
    <FullWidthSpace
      className='scroller--y qa-header-form__required-fields'
      style={{
        width: 300,
        maxHeight: 350,
        padding: '0px 8px',
        margin: '0px',
      }}
    >
      {groupRequiredFields.map((groupItem, index) => {
        const isHiddenGroup = hiddenGroups?.includes(groupItem.groupName);
        const isAdditionalGroup = additionalGroupList?.includes(
          groupItem.groupName
        );
        const isVisibleAdditionalGroup =
          isAdditionalGroup && additionalGroup?.includes(groupItem.groupName);

        const isVisibleGroup =
          (!isAdditionalGroup || isVisibleAdditionalGroup) && !isHiddenGroup;

        return isVisibleGroup ? (
          <ErrorGroup
            type='parent'
            key={index}
            groupItem={groupItem}
            productId={productId}
            checkValidRequiredField={checkValidRequiredField}
          />
        ) : null;
      })}

      {/* Contact section */}
      {/* {requiredContactFields.length > 0 && ( */}
      <ErrorLayout header='Contacts, Locations & Certificates' type='parent'>
        {CONTACT_SECTION_FIELDS.map((field, idx) => {
          const isMissing = requiredContactFields.includes(field);
          return (
            <ErrorItem key={idx} label={field}>
              {isMissing ? iconMessage['error'] : iconMessage['success']}
            </ErrorItem>
          );
        })}
      </ErrorLayout>
      {/* )} */}
    </FullWidthSpace>
  );
};

const ErrorGroup = ({ type, groupItem, checkValidRequiredField }) => {
  const headerExtra = groupItem.tooltip ? (
    <Tooltip title={groupItem.tooltip}>
      <QuestionCircleOutlined />
    </Tooltip>
  ) : null;

  return (
    <ErrorLayout
      header={groupItem.groupName}
      type={type}
      headerExtra={headerExtra}
    >
      {groupItem?.fields?.map((field) => {
        const isValidField = checkValidRequiredField({
          field,
        });

        const label = field.displayName;

        const icon = isValidField
          ? iconMessage['success']
          : iconMessage['error'];

        return (
          <ErrorItem key={field} label={label}>
            {icon}
          </ErrorItem>
        );
      })}
      {groupItem?.childGroups?.length > 0 ? (
        <FullWidthSpace>
          {groupItem?.childGroups?.map((groupItem, index) => {
            return (
              <ErrorGroup
                key={index}
                type='child'
                groupItem={groupItem}
                checkValidRequiredField={checkValidRequiredField}
              />
            );
          })}
        </FullWidthSpace>
      ) : null}
    </ErrorLayout>
  );
};

const iconMessage = {
  error: <CloseOutlined style={{ color: '#cf1322' }} />,
  success: <CheckOutlined style={{ marginRight: 0, color: '#389e0d' }} />,
};

const ErrorLayout = ({ header, type, headerExtra, children, ...rest }) => {
  return (
    <div {...rest}>
      <Row wrap={false} gutter={8} style={{ alignItems: 'baseline' }}>
        <Col flex={1} style={{ minWidth: 0 }}>
          <Typography.Text
            style={{
              fontSize: 14,
              fontWeight:
                type === 'parent' ? 'bold' : 'child' ? 600 : undefined,
            }}
          >
            {header}
          </Typography.Text>
        </Col>

        <Col flex={0}>{headerExtra ? headerExtra : null}</Col>
      </Row>

      <div style={{ paddingLeft: type === 'child' ? 8 : undefined }}>
        {children}
      </div>
    </div>
  );
};

const ErrorItem = ({ label, children, flexIcon, ...rest }) => {
  return (
    <Row wrap={false} style={{ margin: '4px 0' }} {...rest}>
      <Col flex={1} style={{ minWidth: 0 }}>
        <Typography.Text>{label}</Typography.Text>
      </Col>
      <Col flex={0}>{children}</Col>
    </Row>
  );
};
